import { useEffect, useMemo, useState } from 'react';
import type { Card, CardResult, Language } from '../../../../shared/types';
import { MODE_LABELS, NATIVE_LANG, type HandsFreeMode } from './handsFreeSteps';
import { DEFAULT_PREFS, loadPrefs, savePrefs, type HandsFreePrefs } from './handsFreePrefs';
import { useHandsFree, type Phase } from './useHandsFree';
import { useRecognition, useRecognitionCapability } from './useRecognition';
import { useSpeaker } from './useSpeaker';

interface Props {
  language: Language;
  session: { current: Card | null; done: boolean; answer: (result: CardResult) => void };
  speechRate: number;
  /**
   * Raised the first time echo runs, so the drill can decline to record the
   * session. Repeating a word after a voice proves you can hear it, not that
   * you know it — marking those cards correct would inflate every streak and
   * push real reviews weeks into the future.
   */
  onPracticeOnly: () => void;
  onStop: () => void;
}

const PHASE_TEXT: Record<Phase, string> = {
  stopped: 'Ready',
  prompting: 'Listen…',
  listening: 'Your turn — speak now',
  checking: 'Checking…',
  revealing: 'Here is the answer',
  finished: 'Finished',
};

export default function HandsFreePanel({
  language,
  session,
  speechRate,
  onPracticeOnly,
  onStop,
}: Props) {
  const [prefs, setPrefs] = useState<HandsFreePrefs>(DEFAULT_PREFS);
  useEffect(() => setPrefs(loadPrefs()), []);

  const update = (patch: Partial<HandsFreePrefs>) => {
    setPrefs((prev) => {
      const next = { ...prev, ...patch };
      savePrefs(next);
      return next;
    });
  };

  const speaker = useSpeaker();
  const recognition = useRecognition({ localOnly: prefs.localOnly });

  // Recall listens in English, the other two in the target language.
  const listenLang = prefs.mode === 'recall' ? NATIVE_LANG : language.speechTag;
  const capability = useRecognitionCapability(listenLang);

  const handsFree = useHandsFree({
    mode: prefs.mode,
    language,
    speaker,
    recognition,
    session,
    listenMs: prefs.listenSeconds * 1000,
    rate: speechRate,
  });

  const { active, start, stop } = handsFree;

  useEffect(() => {
    if (prefs.mode === 'echo' && active) onPracticeOnly();
  }, [prefs.mode, active, onPracticeOnly]);

  const targetVoice = speaker.voiceFor(language.speechTag);
  const nativeVoice = speaker.voiceFor(NATIVE_LANG);

  // Which voices this mode actually needs — production reads the English side
  // aloud, which no other part of the app has ever required.
  const needsNative = prefs.mode === 'production';
  const missingVoice = !targetVoice || (needsNative && !nativeVoice);

  const needsMicrophone = prefs.mode !== 'echo';
  const blocked = missingVoice || (needsMicrophone && !capability.supported);

  const offlineNote = useMemo(() => {
    const bits: string[] = [];
    if (targetVoice) {
      bits.push(
        `${language.name}: ${targetVoice.name} (${targetVoice.localService ? 'on this device' : 'over the network'})`,
      );
    }
    if (needsNative && nativeVoice) {
      bits.push(
        `English: ${nativeVoice.name} (${nativeVoice.localService ? 'on this device' : 'over the network'})`,
      );
    }
    return bits;
  }, [targetVoice, nativeVoice, needsNative, language.name]);

  return (
    <section className="handsfree">
      <header className="handsfree-head">
        <h2>Hands-free</h2>
        <button type="button" className="btn btn--small" onClick={onStop}>
          Back to tapping
        </button>
      </header>

      {!active && (
        <>
          <div className="handsfree-modes">
            {(Object.keys(MODE_LABELS) as HandsFreeMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                className={mode === prefs.mode ? 'btn btn--primary handsfree-mode' : 'btn handsfree-mode'}
                onClick={() => update({ mode })}
              >
                <span className="handsfree-mode-name">{MODE_LABELS[mode].name}</span>
                <span className="handsfree-mode-blurb">{MODE_LABELS[mode].blurb}</span>
              </button>
            ))}
          </div>

          <div className="handsfree-settings">
            <label className="handsfree-field">
              <span>Time to answer</span>
              <input
                type="range"
                min={2}
                max={20}
                step={1}
                value={prefs.listenSeconds}
                onChange={(e) => update({ listenSeconds: Number(e.target.value) })}
              />
              <span className="muted">{prefs.listenSeconds}s</span>
            </label>

            {needsMicrophone && (
              <label className="handsfree-check">
                <input
                  type="checkbox"
                  checked={prefs.localOnly}
                  onChange={(e) => update({ localOnly: e.target.checked })}
                />
                <span>
                  Only recognise on this device
                  <span className="muted handsfree-hint">
                    {capability.checking
                      ? ' — checking…'
                      : capability.processing === 'on-device'
                        ? ' — available for this language'
                        : capability.installable
                          ? ' — a language pack can be downloaded'
                          : ' — not available in this browser'}
                  </span>
                </span>
              </label>
            )}

            {needsMicrophone && capability.installable && (
              <button type="button" className="btn btn--small" onClick={() => capability.install()}>
                Download the on-device language pack
              </button>
            )}
          </div>

          {/* The one thing worth being loud about: by default, Chrome sends
              your microphone audio to Google to transcribe it. */}
          {needsMicrophone && capability.processing === 'network' && (
            <p className="callout handsfree-privacy">
              This browser transcribes speech on Google's servers, so this mode needs a connection
              and your voice leaves the device. <strong>Listen and repeat</strong> needs no
              microphone and works offline.
            </p>
          )}

          {offlineNote.length > 0 && (
            <p className="muted handsfree-voices">{offlineNote.join(' · ')}</p>
          )}

          {missingVoice && (
            <p className="error">
              {!targetVoice
                ? `There is no ${language.name} voice on this device, so nothing can be read aloud.`
                : 'There is no English voice on this device, which this mode needs to read the prompt.'}
            </p>
          )}

          {needsMicrophone && !capability.supported && (
            <p className="error">
              This browser has no speech recognition, so it cannot hear your answer. Listen and
              repeat still works.
            </p>
          )}

          {handsFree.problem && <p className="error">{handsFree.problem}</p>}

          <button
            type="button"
            className="btn btn--primary btn--large btn--block"
            disabled={blocked}
            onClick={start}
          >
            Start hands-free
          </button>

          <p className="muted handsfree-commands">
            While it is listening you can say <em>repeat</em>, <em>skip</em>, <em>stop</em>, or{' '}
            <em>I was right</em> to overrule the marking.
          </p>
        </>
      )}

      {active && (
        <div className="handsfree-running">
          <p className={`handsfree-phase handsfree-phase--${handsFree.phase}`}>
            {PHASE_TEXT[handsFree.phase]}
          </p>

          {/* Showing the live transcript is what makes a mishearing legible
              rather than mysterious — you can see it heard "the bank". */}
          <p className="handsfree-heard" aria-live="polite">
            {handsFree.interim || ' '}
          </p>

          {/* A notice is not a verdict: nothing was heard, so nothing was
              marked, and the card is being asked again. */}
          {handsFree.notice && <p className="handsfree-notice">{handsFree.notice}</p>}

          {handsFree.lastReport && !handsFree.notice && (
            <p className={`handsfree-verdict handsfree-verdict--${handsFree.lastReport.verdict}`}>
              {handsFree.lastReport.verdict === 'match' && 'Right'}
              {handsFree.lastReport.verdict === 'close' && `Close — ${handsFree.lastReport.expected}`}
              {handsFree.lastReport.verdict === 'miss' && `It was ${handsFree.lastReport.expected}`}
              {handsFree.lastReport.heard && (
                <span className="muted handsfree-heard-was"> (heard “{handsFree.lastReport.heard}”)</span>
              )}
            </p>
          )}

          <div className="handsfree-controls">
            <button type="button" className="btn" onClick={handsFree.repeat}>
              Repeat
            </button>
            <button type="button" className="btn" onClick={handsFree.skip}>
              Skip
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => handsFree.override('correct')}
              disabled={!handsFree.lastReport}
            >
              I was right
            </button>
            <button type="button" className="btn btn--danger" onClick={stop}>
              Stop
            </button>
          </div>

          {prefs.mode === 'echo' && (
            <p className="muted handsfree-note">
              Practice only — repeating a word proves you heard it, not that you know it, so this
              run is not recorded against your review schedule.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
